import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Container, Divider, FormControl, FormControlLabel, Grid, InputLabel,
  MenuItem, Paper, Select, Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScienceIcon from '@mui/icons-material/Science';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import TitrationStation from './TitrationStation';
import StepGuide, { type GuideStep } from './StepGuide';
import Notebook, { type ReportContext, type TitrationReading } from './Notebook';
import {
  ConcordanceTable, IndicatorChoiceVisualiser, PHCurvePlot, StoichiometryWalkthrough, type PHPoint,
} from './LearningAids';
import './titration.css';

type Family = 'acid-base' | 'redox' | 'precipitation' | 'complexometric';
type Mode = 'unknown-concentration' | 'standardize' | 'rough-accurate' | 'indicator-choice' | 'redox' | 'precipitation' | 'complexometric' | 'back-titration' | 'error-investigation' | 'sandbox';

interface MethodOption {
  id: string;
  family: Family;
  name: string;
  analyte: string;
  titrant: string;
  equation: string;
  indicatorId: string;
  coefficientAnalyte: number;
  coefficientTitrant: number;
  acidBasePairId?: string;
  transition?: { min: number; max: number };
  note: string;
}

const METHODS: MethodOption[] = [
  { id: 'strong-acid-strong-base', family: 'acid-base', name: 'Hydrochloric acid + sodium hydroxide', analyte: 'Hydrochloric acid (HCl)', titrant: 'Sodium hydroxide (NaOH)', equation: 'HCl + NaOH -> NaCl + H2O', indicatorId: 'methyl-orange', coefficientAnalyte: 1, coefficientTitrant: 1, acidBasePairId: 'strong-acid-strong-base', transition: { min: 3.1, max: 4.4 }, note: 'Strong acid/strong base: the equivalence point is near pH 7.' },
  { id: 'weak-acid-strong-base', family: 'acid-base', name: 'Ethanoic acid + sodium hydroxide', analyte: 'Ethanoic acid (CH3COOH)', titrant: 'Sodium hydroxide (NaOH)', equation: 'CH3COOH + NaOH -> CH3COONa + H2O', indicatorId: 'phenolphthalein', coefficientAnalyte: 1, coefficientTitrant: 1, acidBasePairId: 'weak-acid-strong-base', transition: { min: 8.2, max: 10 }, note: 'Weak acid/strong base: the equivalence solution is alkaline.' },
  { id: 'strong-acid-weak-base', family: 'acid-base', name: 'Hydrochloric acid + ammonia', analyte: 'Hydrochloric acid (HCl)', titrant: 'Ammonia solution (NH3)', equation: 'HCl + NH3 -> NH4Cl', indicatorId: 'methyl-orange', coefficientAnalyte: 1, coefficientTitrant: 1, acidBasePairId: 'strong-acid-weak-base', transition: { min: 3.1, max: 4.4 }, note: 'Strong acid/weak base: the equivalence solution is acidic.' },
  { id: 'weak-acid-weak-base', family: 'acid-base', name: 'Ethanoic acid + ammonia (instrumental endpoint)', analyte: 'Ethanoic acid (CH3COOH)', titrant: 'Ammonia solution (NH3)', equation: 'CH3COOH + NH3 -> NH4CH3COO', indicatorId: 'phenolphthalein', coefficientAnalyte: 1, coefficientTitrant: 1, acidBasePairId: 'weak-acid-weak-base', transition: { min: 8.2, max: 10 }, note: 'Weak acid/weak base: no sharp visual endpoint; use an instrumental method.' },
  { id: 'permanganate-iron(ii)', family: 'redox', name: 'Potassium manganate(VII) + iron(II)', analyte: 'Iron(II) ions (Fe2+)', titrant: 'Potassium manganate(VII) (KMnO4)', equation: 'MnO4- + 8H+ + 5Fe2+ -> Mn2+ + 4H2O + 5Fe3+', indicatorId: 'potassium-permanganate', coefficientAnalyte: 5, coefficientTitrant: 1, note: 'Acidify with dilute sulfuric acid. KMnO4 is self-indicating; endpoint is first permanent pale pink.' },
  { id: 'permanganate-oxalate', family: 'redox', name: 'Potassium manganate(VII) + oxalate', analyte: 'Oxalate ions (C2O4 2-)', titrant: 'Potassium manganate(VII) (KMnO4)', equation: '2MnO4- + 16H+ + 5C2O4 2- -> 2Mn2+ + 8H2O + 10CO2', indicatorId: 'potassium-permanganate', coefficientAnalyte: 5, coefficientTitrant: 2, note: 'Use sulfuric acid and warm the flask to about 60 °C before titration.' },
  { id: 'dichromate-iron(ii)', family: 'redox', name: 'Potassium dichromate(VI) + iron(II)', analyte: 'Iron(II) ions (Fe2+)', titrant: 'Potassium dichromate(VI) (K2Cr2O7)', equation: 'Cr2O7 2- + 14H+ + 6Fe2+ -> 2Cr3+ + 7H2O + 6Fe3+', indicatorId: 'diphenylamine-sulfonate', coefficientAnalyte: 6, coefficientTitrant: 1, note: 'Use sulfuric acid; orange dichromate forms green Cr3+. The indicator gives a violet-blue endpoint.' },
  { id: 'iodine-thiosulfate', family: 'redox', name: 'Iodine + sodium thiosulfate', analyte: 'Iodine (I2)', titrant: 'Sodium thiosulfate (Na2S2O3)', equation: 'I2 + 2S2O3 2- -> 2I- + S4O6 2-', indicatorId: 'starch', coefficientAnalyte: 1, coefficientTitrant: 2, note: 'Add starch near the pale-yellow stage; endpoint is disappearance of blue-black colour.' },
  { id: 'permanganate-hydrogen-peroxide', family: 'redox', name: 'Potassium manganate(VII) + hydrogen peroxide', analyte: 'Hydrogen peroxide (H2O2)', titrant: 'Potassium manganate(VII) (KMnO4)', equation: '2MnO4- + 6H+ + 5H2O2 -> 2Mn2+ + 8H2O + 5O2', indicatorId: 'potassium-permanganate', coefficientAnalyte: 5, coefficientTitrant: 2, note: 'Use sulfuric acid. KMnO4 is self-indicating; endpoint is first permanent pale pink.' },
  { id: 'mohr-chloride', family: 'precipitation', name: 'Mohr chloride determination', analyte: 'Chloride ions (Cl-)', titrant: 'Silver nitrate (AgNO3)', equation: 'Ag+ + Cl- -> AgCl(s)', indicatorId: 'potassium-chromate', coefficientAnalyte: 1, coefficientTitrant: 1, note: 'Maintain near-neutral pH (about 6.5–10); red-brown Ag2CrO4 marks the endpoint.' },
  { id: 'volhard-chloride', family: 'precipitation', name: 'Volhard chloride back-titration', analyte: 'Excess silver ions (Ag+)', titrant: 'Thiocyanate ions (SCN-)', equation: 'Ag+ + SCN- -> AgSCN(s)', indicatorId: 'ferric-ammonium-sulfate', coefficientAnalyte: 1, coefficientTitrant: 1, note: 'Back-titrate excess silver in nitric acid medium; endpoint is the first persistent blood-red complex.' },
  { id: 'fajans-chloride', family: 'precipitation', name: 'Fajans chloride determination', analyte: 'Chloride ions (Cl-)', titrant: 'Silver nitrate (AgNO3)', equation: 'Ag+ + Cl- -> AgCl(s)', indicatorId: 'fluorescein', coefficientAnalyte: 1, coefficientTitrant: 1, note: 'Adsorption endpoint: fluorescein becomes adsorbed on the slightly positive AgCl surface.' },
  { id: 'edta-water-hardness', family: 'complexometric', name: 'EDTA water hardness determination', analyte: 'Calcium and magnesium ions (Ca2+/Mg2+)', titrant: 'EDTA solution', equation: 'M2+ + Y4- -> MY2-', indicatorId: 'eriochrome-black-t', coefficientAnalyte: 1, coefficientTitrant: 1, note: 'Use ammonia-ammonium chloride buffer at pH 10; EBT changes wine-red to blue.' },
  { id: 'edta-calcium', family: 'complexometric', name: 'EDTA calcium determination', analyte: 'Calcium ions (Ca2+)', titrant: 'EDTA solution', equation: 'Ca2+ + Y4- -> CaY2-', indicatorId: 'murexide', coefficientAnalyte: 1, coefficientTitrant: 1, note: 'Use alkaline calcium buffer; murexide changes from metal-bound pink to free purple.' },
];

const FAMILY_LABELS: Record<Family, string> = {
  'acid-base': 'Acid–base', redox: 'Redox', precipitation: 'Precipitation', complexometric: 'Complexometric',
};
const MODE_LABELS: Record<Mode, string> = {
  'unknown-concentration': 'Unknown concentration', standardize: 'Standardise a solution', 'rough-accurate': 'Rough and accurate titres',
  'indicator-choice': 'Choose an indicator', redox: 'Redox titration', precipitation: 'Precipitation titration',
  complexometric: 'Complexometric titration', 'back-titration': 'Back titration', 'error-investigation': 'Investigate errors', sandbox: 'Free play',
};
const calculateTitreSetConcordance = (titresCm3: number[], roughTitreIndex: number | null = 0) => {
  const accurate = titresCm3.map((titre, index) => ({ titre, index })).filter(({ index }) => index !== roughTitreIndex).sort((left, right) => left.titre - right.titre);
  let bestCluster: Array<{ titre: number; index: number }> = [];
  for (let start = 0; start < accurate.length; start += 1) {
    for (let end = start; end < accurate.length; end += 1) {
      if (accurate[end].titre - accurate[start].titre > 0.1 + Number.EPSILON) break;
      const cluster = accurate.slice(start, end + 1);
      if (cluster.length > bestCluster.length) bestCluster = cluster;
    }
  }
  const concordantIndices = bestCluster.map(({ index }) => index);
  return {
    isConcordant: accurate.length >= 2 && bestCluster.length === accurate.length,
    concordantIndices,
    meanTitreCm3: bestCluster.length ? bestCluster.reduce((sum, item) => sum + item.titre, 0) / bestCluster.length : null,
  };
};

const calculateTitreUncertaintyCm3 = () => 0.05 + 0.05;

const calculateConcentrationUncertainty = (concentration: number, titre: number, aliquot: number) => {
  const relativeUncertainty = Math.sqrt((calculateTitreUncertaintyCm3() / titre) ** 2 + (0.05 / aliquot) ** 2);
  return {
    concentrationMolPerDm3: concentration,
    absoluteUncertaintyMolPerDm3: concentration * relativeUncertainty,
    relativeUncertainty,
    relativePercent: relativeUncertainty * 100,
  };
};

const volumeForEquivalence = (method: MethodOption, analyteConcentration: number, aliquotCm3: number, titrantConcentration: number) =>
  ((analyteConcentration * aliquotCm3 * method.coefficientTitrant) / (method.coefficientAnalyte * titrantConcentration));

const TitrationLab: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [family, setFamily] = useState<Family>('acid-base');
  const [methodId, setMethodId] = useState(METHODS[0].id);
  const [mode, setMode] = useState<Mode>('unknown-concentration');
  const method = METHODS.find((item) => item.id === methodId) || METHODS[0];
  const [titrantConcentration, setTitrantConcentration] = useState('0.100');
  const [analyteConcentration, setAnalyteConcentration] = useState('0.100');
  const [silverConcentration, setSilverConcentration] = useState('0.100');
  const [silverAddedVolumeCm3, setSilverAddedVolumeCm3] = useState('30.00');
  const [aliquotCm3, setAliquotCm3] = useState('25.00');
  const [indicatorId, setIndicatorId] = useState(method.indicatorId);
  const [acidMedium, setAcidMedium] = useState('sulfuric acid');
  const [temperatureC, setTemperatureC] = useState('25');
  const [pH, setPH] = useState('10');
  const [bufferId, setBufferId] = useState('ammonia-ammonium-chloride');
  const [buretteRinsed, setBuretteRinsed] = useState(false);
  const [pipetteRinsed, setPipetteRinsed] = useState(false);
  const [buretteFilled, setBuretteFilled] = useState(false);
  const [airBubbleRemoved, setAirBubbleRemoved] = useState(false);
  const [aliquotDelivered, setAliquotDelivered] = useState(false);
  const [indicatorAdded, setIndicatorAdded] = useState(false);
  const [indicatorDrops, setIndicatorDrops] = useState(2);
  const [swirled, setSwirled] = useState(false);
  const [dropwise, setDropwise] = useState(false);
  const [initialReadingCm3, setInitialReadingCm3] = useState('0.00');
  const [buretteReadingCm3, setBuretteReadingCm3] = useState(0);
  const [flaskColour, setFlaskColour] = useState('#ffffff');
  const [dropActive, setDropActive] = useState(false);
  const [swirling, setSwirling] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [stopwatchRunning, setStopwatchRunning] = useState(false);
  const [readings, setReadings] = useState<TitrationReading[]>([]);
  const [result, setResult] = useState<any>(null);
  const [curve, setCurve] = useState<PHPoint[]>([]);
  const [endpointMessage, setEndpointMessage] = useState('Prepare the apparatus to begin.');
  const [conditionIssues, setConditionIssues] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [hintMode, setHintMode] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conclusion, setConclusion] = useState('');
  const [safetyFaults, setSafetyFaults] = useState({ pipetteFillerUsed: true, wasteDisposedCorrectly: true, washDownSides: true });

  const familyMethods = useMemo(() => METHODS.filter((item) => item.family === family), [family]);
  const cTitrant = Number(titrantConcentration);
  const cAnalyte = Number(analyteConcentration);
  const aliquot = Number(aliquotCm3);
  const equivalenceVolumeCm3 = useMemo(() => {
    if (![cTitrant, cAnalyte, aliquot].every(Number.isFinite) || cTitrant <= 0 || cAnalyte <= 0 || aliquot <= 0) return 0;
    if (method.id === 'volhard-chloride') {
      const excessSilverMoles = (Number(silverConcentration) * Number(silverAddedVolumeCm3)) / 1000;
      const chlorideMoles = (cAnalyte * aliquot) / 1000;
      return Math.max(0, ((excessSilverMoles - chlorideMoles) / cTitrant) * 1000);
    }
    return volumeForEquivalence(method, cAnalyte, aliquot, cTitrant);
  }, [method, cAnalyte, aliquot, cTitrant, silverConcentration, silverAddedVolumeCm3]);
  const selectedIndicator = useMemo(() => ({
    'phenolphthalein': { name: 'Phenolphthalein', range: { min: 8.2, max: 10 } },
    'methyl-orange': { name: 'Methyl orange', range: { min: 3.1, max: 4.4 } },
    'methyl-red': { name: 'Methyl red', range: { min: 4.4, max: 6.2 } },
    'bromothymol-blue': { name: 'Bromothymol blue', range: { min: 6, max: 7.6 } },
  } as Record<string, { name: string; range: { min: number; max: number } }>)[indicatorId], [indicatorId]);
  const titreUncertaintyCm3 = calculateTitreUncertaintyCm3();

  useEffect(() => {
    setMethodId(familyMethods[0]?.id || '');
  }, [family, familyMethods]);

  useEffect(() => {
    if (!method) return;
    setIndicatorId(method.indicatorId);
    if (method.id === 'volhard-chloride') setAcidMedium('nitric acid');
    else if (method.family === 'redox') setAcidMedium('sulfuric acid');
    if (method.family === 'complexometric') {
      setPH(method.id === 'edta-water-hardness' ? '10' : '12');
      setBufferId(method.id === 'edta-water-hardness' ? 'ammonia-ammonium-chloride' : 'alkaline-calcium-buffer');
    }
    setBuretteReadingCm3(0);
    setInitialReadingCm3('0.00');
    setReadings([]);
    setResult(null);
    setCurve([]);
    setConditionIssues([]);
    setSessionId(null);
    setSessionLoaded(false);
    setBuretteRinsed(false);
    setPipetteRinsed(false);
    setBuretteFilled(false);
    setAirBubbleRemoved(false);
    setAliquotDelivered(false);
    setIndicatorAdded(false);
  }, [method]);

  useEffect(() => {
    if (method.family !== 'acid-base' || !equivalenceVolumeCm3 || !Number.isFinite(cTitrant)) {
      setCurve([]);
      return;
    }
    let mounted = true;
    api.post('/titration/chemistry/acid-base-curve', {
      pairId: method.acidBasePairId,
      acidConcentrationMolPerDm3: cAnalyte,
      acidVolumeCm3: aliquot,
      baseConcentrationMolPerDm3: cTitrant,
      maxBaseVolumeCm3: Math.max(50, equivalenceVolumeCm3 * 1.6),
      points: 181,
    }).then((response) => {
      if (!mounted) return;
      setCurve(response.data?.data?.curve || []);
    }).catch(() => {
      if (mounted) setCurve([]);
    });
    return () => { mounted = false; };
  }, [method, cAnalyte, aliquot, cTitrant, equivalenceVolumeCm3]);

  useEffect(() => {
    if (!stopwatchRunning) return undefined;
    const timer = window.setInterval(() => setSeconds((value) => value + 0.1), 100);
    return () => window.clearInterval(timer);
  }, [stopwatchRunning]);

  useEffect(() => {
    if (!sessionId || !sessionLoaded) return;
    const timer = window.setTimeout(() => {
      const state = {
        family, methodId, mode, initialReadingCm3, buretteReadingCm3, flaskColour, readings, conclusion,
        preparation: { buretteRinsed, pipetteRinsed, buretteFilled, airBubbleRemoved, aliquotDelivered, indicatorAdded },
        cTitrant, cAnalyte, aliquotCm3: aliquot,
      };
      setSaving(true);
      api.patch(`/titration/session/${sessionId}`, { state, readings, conclusion, mode })
        .catch(() => setError('Could not autosave your current titration session.'))
        .finally(() => setSaving(false));
    }, 700);
    return () => window.clearTimeout(timer);
  }, [sessionId, sessionLoaded, family, methodId, mode, initialReadingCm3, buretteReadingCm3, flaskColour, readings, conclusion, buretteRinsed, pipetteRinsed, buretteFilled, airBubbleRemoved, aliquotDelivered, indicatorAdded, cTitrant, cAnalyte, aliquot]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setSessionLoaded(true);
      return;
    }
    let mounted = true;
    api.get('/titration/session').then(async (response) => {
      const sessions = Array.isArray(response.data?.data) ? response.data.data : [];
      const existing = sessions.find((session: any) => session.family === family && session.methodId === methodId && session.status === 'in-progress');
      if (!existing) return;
      const detail = await api.get(`/titration/session/${existing._id}`);
      if (!mounted) return;
      const savedState = detail.data?.data?.state || {};
      setSessionId(existing._id);
      setMode(savedState.mode || existing.mode || 'unknown-concentration');
      if (savedState.family === family && savedState.methodId === methodId) {
        setBuretteReadingCm3(Number(savedState.buretteReadingCm3) || 0);
        setInitialReadingCm3(String(Number(savedState.initialReadingCm3) || 0));
        setFlaskColour(savedState.flaskColour || '#ffffff');
        setReadings(Array.isArray(savedState.readings) ? savedState.readings : []);
        setConclusion(savedState.conclusion || '');
        setBuretteRinsed(Boolean(savedState.preparation?.buretteRinsed));
        setPipetteRinsed(Boolean(savedState.preparation?.pipetteRinsed));
        setBuretteFilled(Boolean(savedState.preparation?.buretteFilled));
        setAirBubbleRemoved(Boolean(savedState.preparation?.airBubbleRemoved));
        setAliquotDelivered(Boolean(savedState.preparation?.aliquotDelivered));
        setIndicatorAdded(Boolean(savedState.preparation?.indicatorAdded));
      }
    }).catch(() => {
      if (mounted) setError('Could not load saved titration sessions.');
    }).finally(() => {
      if (mounted) setSessionLoaded(true);
    });
    return () => { mounted = false; };
  }, [authLoading, isAuthenticated, family, methodId]);

  const flaskPH = useMemo(() => {
    if (!curve.length) return null;
    return curve.reduce((nearest, point) => Math.abs(point.baseVolumeCm3 - buretteReadingCm3) < Math.abs(nearest.baseVolumeCm3 - buretteReadingCm3) ? point : nearest, curve[0]).pH;
  }, [curve, buretteReadingCm3]);

  const currentOnset = method.family === 'acid-base' && selectedIndicator
    ? curve.length ? curve.filter((point) => point.pH >= selectedIndicator.range.min && point.pH <= selectedIndicator.range.max).map((point) => point.baseVolumeCm3) : []
    : [];
  const visualEndpointVolume = currentOnset.length ? currentOnset.reduce((sum, value) => sum + value, 0) / currentOnset.length : equivalenceVolumeCm3;
  const wrongIndicator = method.family === 'acid-base'
    ? Boolean(method.transition && selectedIndicator && (
      (selectedIndicator.range.max < 6 && method.acidBasePairId === 'weak-acid-strong-base')
      || (selectedIndicator.range.min > 8 && method.acidBasePairId === 'strong-acid-weak-base')
      || !currentOnset.length
    ))
    : indicatorId !== method.indicatorId;
  const invalidConditions = conditionIssues.length > 0;
  const canAssessEndpoint = !wrongIndicator && !invalidConditions;
  const acidBaseEndpointReached = method.family === 'acid-base' && buretteReadingCm3 > 0 && currentOnset.length > 0
    && Math.min(...currentOnset.map((value) => Math.abs(value - buretteReadingCm3))) <= 0.16;
  const atEndpoint = canAssessEndpoint && (
    method.family === 'acid-base' ? acidBaseEndpointReached : Math.abs(buretteReadingCm3 - equivalenceVolumeCm3) <= 0.12
  );
  const canTitrate = buretteRinsed && pipetteRinsed && buretteFilled && airBubbleRemoved && aliquotDelivered && indicatorAdded
    && safetyFaults.pipetteFillerUsed && safetyFaults.wasteDisposedCorrectly && canAssessEndpoint;
  const concordance = readings.length ? calculateTitreSetConcordance(readings.map((reading) => reading.titreCm3)) : null;
  const meanTitre = concordance?.meanTitreCm3 ?? null;
  const accurateReadings = readings.filter((reading) => reading.runType === 'accurate');
  const setConcordanceFlags = (items: TitrationReading[]) => {
    const accurateIndices = items.map((item, index) => ({ item, index })).filter(({ item }) => item.runType === 'accurate');
    if (accurateIndices.length < 2) return items.map((item) => ({ ...item, concordant: item.runType === 'rough' ? null : null }));
    const roughIndex = items.findIndex((item) => item.runType === 'rough');
    const accurateSet = calculateTitreSetConcordance(items.map((item) => item.titreCm3), roughIndex < 0 ? null : roughIndex);
    const accepted = new Set(accurateSet.concordantIndices);
    return items.map((item, index) => ({ ...item, concordant: item.runType === 'rough' ? null : accepted.has(index) }));
  };

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const response = await api.post('/titration/session', {
      family: method.family,
      methodId: method.id,
      mode,
      title: method.name,
      state: { family: method.family, methodId: method.id, mode },
      readings: [],
    });
    const id = response.data?.data?._id as string;
    setSessionId(id);
    setSessionLoaded(true);
    return id;
  };

  const startLab = async () => {
    setError('');
    try {
      const conditionResponse = await api.post('/titration/chemistry/validate-procedure', {
        family: method.family,
        methodId: method.id,
        indicatorId,
        completedSteps: [],
        conditions: {
          acid: acidMedium,
          medium: acidMedium,
          temperatureC: Number(temperatureC),
          pH: Number(pH),
          bufferId,
          pipetteFillerUsed: safetyFaults.pipetteFillerUsed,
          buretteAirBubble: false,
          wasteDisposedCorrectly: safetyFaults.wasteDisposedCorrectly,
          indicatorDrops,
          washDownSides: safetyFaults.washDownSides,
        },
      });
      const issues = conditionResponse.data?.data?.conditionIssues || [];
      setConditionIssues(issues);
      if (issues.length) {
        setEndpointMessage(`Check the method conditions before relying on the endpoint: ${issues.join(' ')}`);
      }
      await ensureSession();
      if (!issues.length) setEndpointMessage('Session ready. Follow the preparation steps, then begin the titration.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not create a titration session. Sign in and try again.');
    }
  };

  const markStep = (step: string, value: boolean) => {
    const setters: Record<string, (checked: boolean) => void> = {
      'rinse-burette': setBuretteRinsed,
      'rinse-pipette': setPipetteRinsed,
      'fill-burette': setBuretteFilled,
      'remove-air-bubble': setAirBubbleRemoved,
      'pipette-aliquot': setAliquotDelivered,
      'add-indicator': setIndicatorAdded,
    };
    setters[step]?.(value);
    if (value) setEndpointMessage(`${step.replace(/-/g, ' ')} completed.`);
  };

  const handleDispense = (volumeCm3: number) => {
    if (!canTitrate) {
      setEndpointMessage('Complete the apparatus, aliquot, and indicator preparation before opening the stopcock.');
      return;
    }
    if (!swirled) {
      setEndpointMessage('Swirl the flask before adding the next portion of titrant.');
      return;
    }
    if (buretteReadingCm3 >= equivalenceVolumeCm3 - 1 && !dropwise && volumeCm3 > 0.1) {
      setEndpointMessage('Near the expected endpoint: select dropwise flow (0.05 cm³) and swirl after every drop.');
      return;
    }
    const next = Math.min(50 - Number(initialReadingCm3), Number((buretteReadingCm3 + volumeCm3).toFixed(2)));
    setBuretteReadingCm3(next);
    setSwirled(false);
    setDropActive(true);
    window.setTimeout(() => setDropActive(false), 600);

    if (method.family === 'acid-base' && flaskPH !== null && selectedIndicator && !wrongIndicator) {
      const afterPH = curve.reduce((nearest, point) => Math.abs(point.baseVolumeCm3 - next) < Math.abs(nearest.baseVolumeCm3 - next) ? point : nearest, curve[0] || { baseVolumeCm3: 0, pH: 7 }).pH;
      const color = afterPH < selectedIndicator.range.min ? (selectedIndicator.name === 'Methyl orange' || selectedIndicator.name === 'Methyl red' ? '#d95043' : '#ffffff')
        : afterPH > selectedIndicator.range.max ? (selectedIndicator.name === 'Phenolphthalein' ? '#ee84aa' : '#e6c840')
          : selectedIndicator.name === 'Methyl orange' ? '#e7a14a' : selectedIndicator.name === 'Bromothymol blue' ? '#54a878' : '#ed9db7';
      setFlaskColour(color);
    } else if (method.family === 'redox') {
      const nearEndpoint = Math.abs(next - equivalenceVolumeCm3) <= 0.12;
      if (method.indicatorId === 'potassium-permanganate') {
        setFlaskColour(nearEndpoint ? '#f2a7ba' : '#f4f4f0');
      } else if (method.id === 'iodine-thiosulfate') {
        const fractionRemaining = equivalenceVolumeCm3 > 0 ? Math.max(0, 1 - next / equivalenceVolumeCm3) : 1;
        setFlaskColour(nearEndpoint ? '#f4f4f0' : fractionRemaining < 0.12 ? '#18204a' : '#8c6334');
      } else if (method.id === 'dichromate-iron(ii)') {
        setFlaskColour(nearEndpoint ? '#514071' : '#66854a');
      }
    } else if (method.family === 'precipitation') {
      const nearEndpoint = Math.abs(next - equivalenceVolumeCm3) <= 0.12;
      if (method.id === 'mohr-chloride') setFlaskColour(nearEndpoint ? '#9d4331' : '#f0e9c8');
      if (method.id === 'volhard-chloride') setFlaskColour(nearEndpoint ? '#a52827' : '#ead9cc');
      if (method.id === 'fajans-chloride') setFlaskColour(nearEndpoint ? '#dd8ab7' : '#f0e9c8');
    } else if (method.family === 'complexometric') {
      setFlaskColour(Math.abs(next - equivalenceVolumeCm3) <= 0.12 ? '#3b83b9' : '#883f59');
    }
    if (wrongIndicator) {
      setEndpointMessage('This indicator transition does not match the expected pH change; select an appropriate indicator before using endpoint colour as a result.');
    } else if (Math.abs(next - visualEndpointVolume) <= 0.12) {
      setEndpointMessage(`Indicator endpoint observed near ${(Number(initialReadingCm3) + next).toFixed(2)} cm³ final reading; titre = ${next.toFixed(2)} cm³. Equivalence titre is ${equivalenceVolumeCm3.toFixed(2)} cm³.`);
    } else if (Math.abs(next - equivalenceVolumeCm3) <= 0.12) {
      setEndpointMessage(`${method.family === 'complexometric' ? 'Indicator endpoint' : 'Endpoint'} reached near ${next.toFixed(2)} cm³ delivered. Confirm the method-specific colour and stop before over-titration.`);
    } else if (next > equivalenceVolumeCm3 + 0.2) {
      setEndpointMessage('The equivalence volume has been passed. Review the colour change and consider this run an over-titration.');
    } else {
      setEndpointMessage('Titrant added. Swirl the flask, wash down the sides when needed, and approach the endpoint carefully.');
    }
  };

  const recordRun = () => {
    if (!canTitrate) {
      setEndpointMessage('Complete all preparation steps before recording a titre.');
      return;
    }
    if (wrongIndicator || invalidConditions) {
      setEndpointMessage('A titre cannot be marked endpoint-complete with an unsuitable indicator. Correct the indicator and repeat.');
      return;
    }
    if (!atEndpoint) {
      setEndpointMessage('The visual endpoint has not been reached yet. Continue carefully in dropwise mode.');
      return;
    }
    const runType = readings.some((reading) => reading.runType === 'rough') ? 'accurate' : 'rough';
    const item: TitrationReading = {
      id: `titre-${Date.now()}`,
      runType,
      aliquotCm3: aliquot,
      indicator: indicatorId,
      initialReadingCm3: Number(initialReadingCm3),
      finalReadingCm3: Number(initialReadingCm3) + buretteReadingCm3,
      titreCm3: (Number(initialReadingCm3) + buretteReadingCm3) - Number(initialReadingCm3),
      endpointColour: flaskColour,
      concordant: null,
      remarks: runType === 'rough' ? 'Rough titre; excluded from accurate mean.' : '',
      timestamp: new Date().toISOString(),
    };
    const nextReadings = setConcordanceFlags([...readings, item]);
    setReadings(nextReadings);
    setBuretteReadingCm3(0);
    setFlaskColour('#ffffff');
    setSwirled(false);
    setDropwise(false);
    setResult(null);
    setEndpointMessage(runType === 'rough' ? 'Rough titre recorded and excluded from the mean. Reset the flask and run accurate titres.' : 'Accurate titre recorded. Repeat until at least two accurate titres agree within ±0.10 cm³.');
  };

  const calculateResult = async () => {
    if (!meanTitre || !concordance?.isConcordant) {
      setError('Record at least two concordant accurate titres before calculating the result.');
      return;
    }
    try {
      const response = await api.post('/titration/chemistry/calculate', method.id === 'volhard-chloride' ? {
        calculation: 'volhard-chloride',
        silverConcentrationMolPerDm3: Number(silverConcentration),
        silverNitrateAddedVolumeCm3: Number(silverAddedVolumeCm3),
        thiocyanateConcentrationMolPerDm3: cTitrant,
        thiocyanateTitreVolumeCm3: meanTitre,
        chlorideSampleVolumeCm3: aliquot,
      } : {
        family: method.family,
        systemId: ['redox', 'complexometric'].includes(method.family) ? method.id : undefined,
        methodId: method.id,
        titrantConcentrationMolPerDm3: cTitrant,
        edtaConcentrationMolPerDm3: cTitrant,
        titreVolumeCm3: meanTitre,
        analyteVolumeCm3: aliquot,
      });
      const calculated = response.data?.data;
      const concentrationMolPerDm3 = calculated.concentrationMolPerDm3 ?? calculated.analyteConcentrationMolPerDm3;
      const uncertainty = calculated.uncertainty || calculateConcentrationUncertainty(concentrationMolPerDm3, meanTitre, aliquot);
      setResult({ ...calculated, concentrationMolPerDm3, uncertainty });
      setEndpointMessage('Concentration calculated from the mean concordant titre and balanced-equation ratio.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Could not calculate the titration result.');
    }
  };

  const currentReading = () => ({
    runType: readings.some((reading) => reading.runType === 'rough') ? 'accurate' as const : 'rough' as const,
    aliquotCm3: aliquot,
    indicator: indicatorId,
    initialReadingCm3: Number(initialReadingCm3),
    finalReadingCm3: Number(initialReadingCm3) + buretteReadingCm3,
    titreCm3: buretteReadingCm3,
    endpointColour: flaskColour,
    concordant: null,
  });

  const addManualNotebookRow = () => {
    setReadings(setConcordanceFlags([...readings, {
      ...currentReading(),
      id: `manual-titre-${Date.now()}`,
      timestamp: new Date().toISOString(),
      remarks: 'Manual notebook row',
    }]));
  };

  const reportContext: ReportContext = {
    studentName: user?.name || 'Student',
    practicalTitle: method.name,
    family: FAMILY_LABELS[method.family],
    method: method.id,
    equation: method.equation,
    result: result ? `${Number(result.concentrationMolPerDm3).toPrecision(4)} mol dm-3` : '',
    uncertainty: result ? `±${Number(result.uncertainty.absoluteUncertaintyMolPerDm3).toPrecision(2)} mol dm-3` : '',
    conclusion,
  };

  const saveReport = async (blob: Blob) => {
    if (!sessionId) throw new Error('Start or save a titration session before exporting the report.');
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('title', `${method.name} laboratory report`);
    formData.append('conclusion', conclusion);
    formData.append('readings', JSON.stringify(readings));
    formData.append('report', blob, 'titration-lab-report.pdf');
    await api.post('/titration/report', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const allSteps: GuideStep[] = [
    { id: 'safety-check', title: 'Safety and waste ready', instruction: 'Wear eye protection, handle the reagents carefully, and identify the correct waste container.', complete: safetyFaults.pipetteFillerUsed && safetyFaults.wasteDisposedCorrectly, issue: !safetyFaults.pipetteFillerUsed ? 'Never mouth-pipette; attach the pipette filler.' : !safetyFaults.wasteDisposedCorrectly ? 'Confirm correct chemical-waste disposal.' : undefined },
    { id: 'rinse-burette', title: 'Rinse burette with titrant', instruction: 'Rinse the burette with distilled water, then with a small volume of its titrant.', complete: buretteRinsed },
    { id: 'rinse-pipette', title: 'Rinse pipette with analyte', instruction: 'Condition the pipette using a little of the analyte solution, not water.', complete: pipetteRinsed },
    { id: 'fill-burette', title: 'Fill burette and set initial level', instruction: 'Fill above zero, open the tap to fill the jet, then record the meniscus reading.', complete: buretteFilled },
    { id: 'remove-air-bubble', title: 'Remove the jet air bubble', instruction: 'Check for and remove any bubble below the stopcock before readings.', complete: airBubbleRemoved, issue: buretteFilled && !airBubbleRemoved ? 'A trapped bubble changes the delivered volume.' : undefined },
    { id: 'record-initial-reading', title: 'Record initial reading', instruction: 'Read the bottom of the meniscus at eye level; the burette scale increases downward.', complete: buretteFilled && airBubbleRemoved },
    { id: 'pipette-aliquot', title: 'Deliver 25.00 cm³ aliquot', instruction: 'Use the pipette filler, align the meniscus to the calibration mark, and deliver into the flask.', complete: aliquotDelivered },
    { id: 'add-indicator', title: 'Add 2–3 indicator drops', instruction: 'Add only a small amount of the selected, chemically suitable indicator.', complete: indicatorAdded, issue: wrongIndicator ? 'This indicator is unsuitable for the chosen titration curve.' : undefined },
    { id: 'perform-rough-titration', title: 'Run a rough titration', instruction: 'Add titrant more quickly at first while continuously swirling.', complete: readings.some((reading) => reading.runType === 'rough') },
    { id: 'swirl-flask', title: 'Swirl to mix each addition', instruction: 'Swirl after each addition; wash down flask walls with distilled water when required.', complete: swirled },
    { id: 'add-dropwise-near-endpoint', title: 'Approach endpoint dropwise', instruction: 'Near the rough endpoint, switch to 0.05 cm³ additions and wait for the colour to settle.', complete: dropwise },
    { id: 'record-final-reading', title: 'Record final burette reading', instruction: 'Record the final meniscus reading, then calculate final minus initial.', complete: readings.length > 0 },
    { id: 'repeat-accurate-titrations', title: 'Repeat accurate titrations', instruction: 'Repeat until at least two accurate titres agree within ±0.10 cm³.', complete: accurateReadings.length >= 2 },
    { id: 'check-concordance', title: 'Confirm concordance', instruction: 'Exclude the rough run; average only accurate concordant titres.', complete: Boolean(concordance?.isConcordant) },
    { id: 'dispose-of-waste', title: 'Dispose of waste correctly', instruction: 'Use the labelled waste container; do not return used solution to reagent bottles.', complete: safetyFaults.wasteDisposedCorrectly },
  ];

  const familyField = (
    <FormControl fullWidth size="small">
      <InputLabel id="titration-family-label">Titration family</InputLabel>
      <Select labelId="titration-family-label" value={family} label="Titration family" onChange={(event) => setFamily(event.target.value as Family)}>
        {(Object.keys(FAMILY_LABELS) as Family[]).map((item) => <MenuItem key={item} value={item}>{FAMILY_LABELS[item]}</MenuItem>)}
      </Select>
    </FormControl>
  );

  const modeField = (
    <FormControl fullWidth size="small">
      <InputLabel id="titration-mode-label">Practical mode</InputLabel>
      <Select labelId="titration-mode-label" value={mode} label="Practical mode" onChange={(event) => setMode(event.target.value as Mode)}>
        {(Object.keys(MODE_LABELS) as Mode[]).map((item) => <MenuItem key={item} value={item}>{MODE_LABELS[item]}</MenuItem>)}
      </Select>
    </FormControl>
  );

  if (authLoading) return <Container sx={{ py: 5 }}><Typography>Loading your laboratory session…</Typography></Container>;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Volumetric Analysis Laboratory</Typography>
          <Typography color="text.secondary">WAEC practical · burette readings, endpoint observation, concordance, and concentration.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/labs')}>All labs</Button>
      </Stack>
      {!isAuthenticated && <Alert severity="info" sx={{ mb: 2 }}>Sign in with a registered account to save titration sessions and reports.</Alert>}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>{familyField}</Grid>
              <Grid item xs={12} sm={6}>{modeField}</Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="titration-method-label">Reaction / method</InputLabel>
                  <Select labelId="titration-method-label" value={method.id} label="Reaction / method" onChange={(event) => setMethodId(String(event.target.value))}>
                    {familyMethods.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><Alert severity={method.family === 'acid-base' && method.id === 'weak-acid-weak-base' ? 'warning' : 'info'}><strong>{method.equation}</strong><br />{method.note}</Alert></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Titrant concentration (mol dm-3)" value={titrantConcentration} onChange={(event) => setTitrantConcentration(event.target.value)} inputProps={{ min: 0.0001, step: 0.001 }} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Analyte concentration (mol dm-3)" value={analyteConcentration} onChange={(event) => setAnalyteConcentration(event.target.value)} inputProps={{ min: 0.0001, step: 0.001 }} helperText={mode === 'unknown-concentration' ? 'Hidden from the student in assessment mode.' : undefined} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Aliquot volume (cm³)" value={aliquotCm3} onChange={(event) => setAliquotCm3(event.target.value)} inputProps={{ min: 1, max: 25, step: 0.05 }} /></Grid>
              {method.id === 'volhard-chloride' && <>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Excess AgNO3 concentration (mol dm-3)" value={silverConcentration} onChange={(event) => setSilverConcentration(event.target.value)} inputProps={{ min: 0.0001, step: 0.001 }} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="AgNO3 added (cm³)" value={silverAddedVolumeCm3} onChange={(event) => setSilverAddedVolumeCm3(event.target.value)} inputProps={{ min: 0.1, max: 50, step: 0.05 }} /></Grid>
              </>}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="titration-indicator-label">Indicator</InputLabel>
                  <Select labelId="titration-indicator-label" value={indicatorId} label="Indicator" onChange={(event) => setIndicatorId(String(event.target.value))}>
                    {Array.from(new Map(METHODS.map((item) => [item.indicatorId, item.indicatorId])).values()).map((id) => <MenuItem key={id} value={id}>{id.replace(/-/g, ' ')}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Indicator drops" value={indicatorDrops} onChange={(event) => setIndicatorDrops(Number(event.target.value))} inputProps={{ min: 1, max: 5, step: 1 }} /></Grid>
              {((method.family === 'redox' && method.note.toLowerCase().includes('sulfuric')) || method.id === 'volhard-chloride') && <Grid item xs={12} sm={4}><FormControl fullWidth size="small"><InputLabel id="acid-medium-label">Acid medium</InputLabel><Select labelId="acid-medium-label" label="Acid medium" value={acidMedium} onChange={(event) => setAcidMedium(String(event.target.value))}><MenuItem value="sulfuric acid">Sulfuric acid</MenuItem><MenuItem value="hydrochloric acid">Hydrochloric acid</MenuItem><MenuItem value="nitric acid">Nitric acid</MenuItem><MenuItem value="none">No added acid</MenuItem></Select></FormControl></Grid>}
              {method.id === 'permanganate-oxalate' && <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Reaction temperature (°C)" value={temperatureC} onChange={(event) => setTemperatureC(event.target.value)} inputProps={{ min: 20, max: 100, step: 1 }} /></Grid>}
              {method.family === 'complexometric' && <>
                <Grid item xs={12} sm={4}><FormControl fullWidth size="small"><InputLabel id="buffer-label">Buffer</InputLabel><Select labelId="buffer-label" value={bufferId} label="Buffer" onChange={(event) => setBufferId(String(event.target.value))}><MenuItem value="ammonia-ammonium-chloride">Ammonia/ammonium chloride</MenuItem><MenuItem value="alkaline-calcium-buffer">Alkaline calcium buffer</MenuItem><MenuItem value="water">Water (no buffer)</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Buffer pH" value={pH} onChange={(event) => setPH(event.target.value)} inputProps={{ min: 0, max: 14, step: 0.1 }} /></Grid>
              </>}
              {method.family === 'precipitation' && <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="number" label="Solution pH" value={pH} onChange={(event) => setPH(event.target.value)} inputProps={{ min: 0, max: 14, step: 0.1 }} /></Grid>}
              <Grid item xs={12}><FormControlLabel control={<Switch checked={hintMode} onChange={(event) => setHintMode(event.target.checked)} />} label="Hint mode" /></Grid>
            </Grid>
          </Paper>

          <TitrationStation buretteReadingCm3={Number(initialReadingCm3) + buretteReadingCm3} flaskColour={flaskColour} stopwatchSeconds={seconds} dropActive={dropActive} swirling={swirling} />

          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
              <Box><Typography variant="h6" fontWeight={700}>Burette and flask controls</Typography><Typography variant="body2" color="text.secondary">Initial: {Number(initialReadingCm3).toFixed(2)} cm³ · Final: {(Number(initialReadingCm3) + buretteReadingCm3).toFixed(2)} cm³ · Titre: {buretteReadingCm3.toFixed(2)} cm³ · Current flask pH: {flaskPH === null ? 'not available' : flaskPH.toFixed(2)}</Typography></Box>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button variant="outlined" onClick={() => { setSwirling(true); setSwirled(true); window.setTimeout(() => setSwirling(false), 850); }}>Swirl flask</Button>
                <Button variant={dropwise ? 'contained' : 'outlined'} onClick={() => setDropwise((value) => !value)}>{dropwise ? 'Dropwise on' : 'Dropwise off'}</Button>
                <Button variant="outlined" onClick={() => setStopwatchRunning((value) => !value)}>{stopwatchRunning ? 'Pause stopwatch' : 'Start stopwatch'}</Button>
                <Button variant="text" onClick={() => { setSeconds(0); setStopwatchRunning(false); }}>Reset time</Button>
              </Stack>
            </Stack>
            <Box sx={{ mt: 2 }}>
              <TextField
                size="small"
                type="number"
                label="Initial burette reading (cm³)"
                value={initialReadingCm3}
                onChange={(event) => setInitialReadingCm3(event.target.value)}
                disabled={!buretteFilled || buretteReadingCm3 > 0}
                inputProps={{ min: 0, max: 50, step: 0.05 }}
                sx={{ mb: 1, minWidth: 220 }}
              />
              <Typography variant="body2" sx={{ mb: 0.5 }}>Stopcock flow · {dropwise ? '0.05 cm³ per drop' : 'select a flow increment'}</Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button variant="contained" disabled={!canTitrate || dropwise} onClick={() => handleDispense(1)}>Fast · 1.00 cm³</Button>
                <Button variant="outlined" disabled={!canTitrate || dropwise} onClick={() => handleDispense(0.2)}>Slow · 0.20 cm³</Button>
                <Button variant="outlined" disabled={!canTitrate || !dropwise} onClick={() => handleDispense(0.05)}>Add one drop · 0.05 cm³</Button>
                <Button variant="contained" color="success" disabled={!atEndpoint || wrongIndicator} onClick={recordRun}>Record {readings.some((reading) => reading.runType === 'rough') ? 'accurate' : 'rough'} titre</Button>
              </Stack>
              <Slider aria-label="Burette delivered volume" value={buretteReadingCm3} min={0} max={Math.max(0, 50 - Number(initialReadingCm3))} step={0.05} onChange={(_, value) => {
                const next = Number(value);
                if (Math.abs(next - buretteReadingCm3) <= 0.051) handleDispense(next - buretteReadingCm3);
              }} sx={{ mt: 1 }} />
            </Box>
            <Alert severity={wrongIndicator || invalidConditions ? 'warning' : atEndpoint ? 'success' : 'info'} sx={{ mt: 1 }}>{endpointMessage}</Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 1 }}>
              <FormControlLabel control={<Switch checked={safetyFaults.pipetteFillerUsed} onChange={(event) => setSafetyFaults((state) => ({ ...state, pipetteFillerUsed: event.target.checked }))} />} label="Pipette filler used" />
              <FormControlLabel control={<Switch checked={safetyFaults.washDownSides} onChange={(event) => setSafetyFaults((state) => ({ ...state, washDownSides: event.target.checked }))} />} label="Wash down flask walls" />
              <FormControlLabel control={<Switch checked={safetyFaults.wasteDisposedCorrectly} onChange={(event) => setSafetyFaults((state) => ({ ...state, wasteDisposedCorrectly: event.target.checked }))} />} label="Correct waste disposal" />
            </Stack>
          </Paper>

          {method.family === 'acid-base' && <Box sx={{ mt: 2 }}><PHCurvePlot curve={curve} currentVolumeCm3={buretteReadingCm3} equivalenceVolumeCm3={equivalenceVolumeCm3} transitionRange={selectedIndicator?.range} /></Box>}
          <Box sx={{ mt: 2 }}>
            <ConcordanceTable readings={readings} meanTitreCm3={meanTitre} needsRepeat={!concordance?.isConcordant} />
          </Box>
          <Box sx={{ mt: 2 }}><Notebook readings={readings} onReadingsChange={(items) => setReadings(setConcordanceFlags(items))} onAutoRecord={currentReading} onManualAdd={addManualNotebookRow} reportContext={reportContext} onSaveReport={saveReport} /></Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={700}>Prepare the practical</Typography>
              {isAuthenticated && <Chip size="small" label={saving ? 'Saving…' : sessionId ? 'Session saved' : 'Not started'} color={saving ? 'warning' : sessionId ? 'success' : 'default'} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>The scale reads 0 at the top and increases downward. Read the bottom of the meniscus for these colourless/pale solutions.</Typography>
            {conditionIssues.length > 0 && <Alert severity="warning" sx={{ mb: 1 }}>{conditionIssues.join(' ')}</Alert>}
            <Stack spacing={0.5}>
              <FormControlLabel control={<Switch checked={buretteRinsed} onChange={(event) => markStep('rinse-burette', event.target.checked)} />} label="Rinse burette with titrant" />
              <FormControlLabel control={<Switch checked={pipetteRinsed} onChange={(event) => markStep('rinse-pipette', event.target.checked)} />} label="Rinse pipette with analyte" />
              <FormControlLabel control={<Switch checked={buretteFilled} onChange={(event) => markStep('fill-burette', event.target.checked)} disabled={!buretteRinsed} />} label="Fill burette and set initial reading" />
              <FormControlLabel control={<Switch checked={airBubbleRemoved} onChange={(event) => markStep('remove-air-bubble', event.target.checked)} disabled={!buretteFilled} />} label="Remove tap air bubble" />
              <FormControlLabel control={<Switch checked={aliquotDelivered} onChange={(event) => markStep('pipette-aliquot', event.target.checked)} disabled={!pipetteRinsed} />} label={`Deliver ${aliquotCm3} cm³ aliquot`} />
              <FormControlLabel control={<Switch checked={indicatorAdded} onChange={(event) => markStep('add-indicator', event.target.checked)} disabled={!aliquotDelivered} />} label="Add 2–3 drops of indicator" />
            </Stack>
            <Button fullWidth variant="contained" startIcon={<ScienceIcon />} sx={{ mt: 1 }} onClick={startLab} disabled={!isAuthenticated}>{sessionId ? 'Recheck conditions and save session' : 'Start and save session'}</Button>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <StepGuide steps={allSteps} hintMode={hintMode} />
          </Paper>
          {method.family === 'acid-base' && <Box sx={{ mb: 2 }}><IndicatorChoiceVisualiser suitable={!wrongIndicator} message={wrongIndicator ? `${indicatorId} does not provide a reliable visual endpoint for this pair. The visual end point and equivalence point are distinct.` : `${indicatorId} transition is compared with the calculated curve. The equivalence volume is ${equivalenceVolumeCm3.toFixed(2)} cm³; current estimated end-point volume is ${visualEndpointVolume.toFixed(2)} cm³.`} /></Box>}
          <StoichiometryWalkthrough
            analyteMoles={result?.analyteMoles ?? null}
            titrantMoles={result?.titrantMoles ?? null}
            ratioText={`${method.coefficientAnalyte} analyte : ${method.coefficientTitrant} titrant`}
            resultText={result ? `${Number(result.concentrationMolPerDm3).toPrecision(4)} mol dm-3 ± ${Number(result.uncertainty.absoluteUncertaintyMolPerDm3).toPrecision(2)} mol dm-3` : ''}
          />
          <Divider sx={{ my: 2 }} />
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6" fontWeight={700}>Analysis and result</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Balanced ratio: {method.coefficientAnalyte} analyte : {method.coefficientTitrant} titrant. Burette titre uncertainty: ±{titreUncertaintyCm3.toFixed(2)} cm³ worst case.</Typography>
            <Button variant="contained" fullWidth onClick={() => void calculateResult()} disabled={!concordance?.isConcordant}>Calculate from concordant titres</Button>
            {result && <Alert severity="success" sx={{ mt: 1 }}><strong>Concentration:</strong> {Number(result.concentrationMolPerDm3).toPrecision(4)} mol dm-3 ± {Number(result.uncertainty.absoluteUncertaintyMolPerDm3).toPrecision(2)} mol dm-3<br /><strong>Standard uncertainty:</strong> {result.uncertainty.relativePercent.toFixed(2)}%</Alert>}
            <TextField fullWidth multiline minRows={2} label="Conclusion" value={conclusion} onChange={(event) => setConclusion(event.target.value)} sx={{ mt: 1.5 }} />
          </Paper>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">Safety: wear eye protection, never mouth-pipette, keep KMnO4 and iodine away from skin, handle dichromate as toxic, and use the correct labelled waste stream.</Typography>
      </Box>
    </Container>
  );
};

export default TitrationLab;